import { ApiService } from 'js/src/client';
import { SubscriptionFailureReasons } from 'js/src/client/types.gen';
import { Toast } from 'js/src/components/common/Toast';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import PaymentService from 'js/src/libs/payment-service';
import { isApiErrorWithProperty } from 'js/src/types';

export type AnimationMinutesPurchaseStatus = 'ok' | 'card_declined' | 'error';

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_RETRIES = 15;
const SECONDS_PER_MINUTE = 60;

export async function purchaseAnimationMinutes(
    name: string,
    addonQuantity: number,
    expectedCycleMinutes: number,
    prorationDate?: number,
): Promise<AnimationMinutesPurchaseStatus> {
    try {
        const response = await ApiService.pricingSubscriptionPartialUpdate({
            body: {
                genx_addon_name: name,
                genx_addon_quantity: addonQuantity,
                proration_date: prorationDate,
            },
        });

        if (response.data.reason === SubscriptionFailureReasons.SCA_REQUIRED) {
            if (!response.data.client_secret) {
                ErrorReporter.capture(new Error('SCA required but client_secret missing'));
                return 'error';
            }
            return handleSCARequired(response.data.client_secret, expectedCycleMinutes);
        }

        return waitForUsageLimitToUpdate(expectedCycleMinutes);
    } catch (error: unknown) {
        if (
            isApiErrorWithProperty(error, 'reason') &&
            error.body.reason === 'credit_card_declined'
        ) {
            return 'card_declined';
        }
        ErrorReporter.capture(error);
        return 'error';
    }
}

async function handleSCARequired(
    clientSecret: string,
    expectedCycleMinutes: number,
): Promise<AnimationMinutesPurchaseStatus> {
    try {
        const result = await PaymentService.confirmCardPayment(clientSecret);
        if (result.paymentIntent) {
            return waitForUsageLimitToUpdate(expectedCycleMinutes);
        }
        Toast.info('Your bank requires additional authentication for this purchase.');
        return 'card_declined';
    } catch (error: unknown) {
        ErrorReporter.capture(error);
        return 'error';
    }
}

/**
 * Poll the usage-limit endpoint (not the subscription endpoint) after a
 * purchase. The subscription quantity reflects what Stripe sees and updates
 * synchronously with the purchase API call — but the team's per-cycle
 * allowance (`animation_seconds_limit_per_cycle`) is set by a separate
 * Stripe webhook handler (`customer.subscription.updated` →
 * `_update_animation_minutes_addon_from_subscription`) which runs
 * asynchronously after Stripe confirms.
 *
 * Polling subscription only confirms that Stripe accepted the change. The
 * GenX page needs to show the user's actual spendable balance immediately
 * (they're about to use those minutes), so we wait for the usage limit to
 * catch up to the new expected total.
 *
 * `expectedCycleMinutes` is the full per-cycle allowance the limit should
 * settle at — the base plan's minutes plus the add-on quantity — not the
 * add-on quantity alone. Polling against the add-on quantity would never
 * match for plans that grant their own minutes.
 */
function waitForUsageLimitToUpdate(
    expectedCycleMinutes: number,
): Promise<AnimationMinutesPurchaseStatus> {
    const expectedLimitSeconds = expectedCycleMinutes * SECONDS_PER_MINUTE;

    return new Promise((resolve) => {
        let retryCount = 0;

        const checkLimit = async () => {
            try {
                const response = await ApiService.usageLimitsRetrieve({ query: {} });
                if (response.data.animation_seconds_limit_per_cycle === expectedLimitSeconds) {
                    resolve('ok');
                } else if (retryCount < MAX_POLL_RETRIES) {
                    retryCount++;
                    setTimeout(checkLimit, POLL_INTERVAL_MS);
                } else {
                    resolve('error');
                }
            } catch (error: unknown) {
                ErrorReporter.capture(error);
                if (retryCount < MAX_POLL_RETRIES) {
                    retryCount++;
                    setTimeout(checkLimit, POLL_INTERVAL_MS);
                } else {
                    resolve('error');
                }
            }
        };

        void checkLimit();
    });
}
